import { memo, useEffect, useRef, useState } from 'react'
import { SLOT_KEY, type GraphicSlot, type StageGraphic } from '@/lib/watch/graphics'
import type { VisibleGraphics } from '@/lib/watch/useStageGraphics'
import type { BroadcastContext } from './context'
import { BroadcastElement } from './registry'
import './broadcast.css'

/** The host's console previewing a graphic on their own canvas (hover or focus), in place of what that slot shows. */
export interface GraphicPreview {
  slot: GraphicSlot
  spec: StageGraphic
}

export interface BroadcastLayerProps {
  /** What the stage's audience sees now (`useStageGraphics`); nothing while no host is live on the stage. */
  graphics: VisibleGraphics
  /** The host's own preview, drawn over their canvas only and marked Preview. */
  preview?: GraphicPreview | null
  context: BroadcastContext
}

/** The theme's large motion duration in milliseconds, as `element` resolves it (0 under reduced motion, or unresolved). */
function largeDuration(element: Element | null): number {
  const value = element ? getComputedStyle(element).getPropertyValue('--motion-large-duration').trim() : ''
  const match = /^(\d*\.?\d+)(ms|s)$/.exec(value)
  return match ? Number(match[1]) * (match[2] === 's' ? 1000 : 1) : 0
}

interface Held {
  spec: StageGraphic
  key: string
  preview: boolean
}

/**
 * One slot: the element its spec names, keyed by the spec, so a new one slides in afresh from the slot's edge; a spec
 * that goes stays a large duration while it slides back out (at once under reduced motion, where the duration is 0).
 */
function Slot({ slot, spec, preview, context }: { slot: GraphicSlot; spec: StageGraphic | null; preview: boolean; context: BroadcastContext }) {
  const ref = useRef<HTMLDivElement>(null)
  const key = spec ? `${preview ? 'preview' : 'air'}:${JSON.stringify(spec)}` : null
  const [held, setHeld] = useState<Held | null>(spec && key ? { spec, key, preview } : null)
  const [leaving, setLeaving] = useState(false)
  if (spec && key && (held?.key !== key || leaving)) {
    // A new spec, or the same one back before it had gone.
    if (held?.key !== key) setHeld({ spec, key, preview })
    setLeaving(false)
  } else if (!spec && held && !leaving) {
    setLeaving(true)
  }
  useEffect(() => {
    if (!leaving) return
    const timer = window.setTimeout(() => {
      setHeld(null)
      setLeaving(false)
    }, largeDuration(ref.current))
    return () => window.clearTimeout(timer)
  }, [leaving])

  return (
    <div ref={ref} className={`bc-slot bc-slot--${slot === 'lower_third' ? 'lower' : 'panel'}`} data-leaving={leaving || undefined}>
      {held && <BroadcastElement key={held.key} spec={spec ?? held.spec} context={context} preview={held.preview} />}
    </div>
  )
}

/**
 * The stage's broadcast elements over the canvas, camera or map (docs/features/badger-cohost.md, Drawing): the lower
 * third bottom centre, clear of the faces (bottom left) and the swap card (bottom right); the panel on the right. Upright
 * on a phone the lower third is one line over the video and the panel shows its top five; on its side, as the desktop.
 * The host's console preview takes its slot on their own canvas, marked Preview. The elements take no pointer: the
 * canvas under them stays the viewer's.
 */
export const BroadcastLayer = memo(function BroadcastLayer({ graphics, preview = null, context }: BroadcastLayerProps) {
  const specFor = (slot: GraphicSlot) => (preview?.slot === slot ? preview.spec : (graphics[SLOT_KEY[slot]] ?? null))
  return (
    <div className="bc-layer" data-compact={context.compact || undefined}>
      <Slot slot="panel" spec={specFor('panel')} preview={preview?.slot === 'panel'} context={context} />
      <Slot slot="lower_third" spec={specFor('lower_third')} preview={preview?.slot === 'lower_third'} context={context} />
    </div>
  )
})
